import React, { useCallback, useEffect, useState } from "react";

import {
  Button,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import { Search } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },
  filters: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    alignItems: "center"
  },
  filterField: { minWidth: 150 },
  searchField: { minWidth: 240, flex: 1 },
  summary: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    padding: theme.spacing(1),
    alignItems: "center"
  },
  summaryCard: {
    padding: theme.spacing(1, 2),
    borderRadius: 8,
    backgroundColor: theme.palette.action.hover,
    minWidth: 120
  },
  summaryValue: { fontSize: 22, fontWeight: 700, lineHeight: 1.2 },
  distribution: { flex: 1, minWidth: 240 },
  distributionRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  barTrack: {
    flex: 1,
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.action.selected
  },
  barFill: {
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.primary.main
  },
  comment: {
    maxWidth: 380,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word"
  },
  muted: { color: theme.palette.text.secondary, fontStyle: "italic" }
}));

const statusLabels = {
  answered: "Respondida",
  pending: "Aguardando",
  sent: "Enviada",
  expired: "Expirada",
  failed: "Falha no envio"
};

const formatDateTime = value => {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleString();
};

const Ratings = () => {
  const classes = useStyles();

  const [ratings, setRatings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);

  const [searchParam, setSearchParam] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [ratingValue, setRatingValue] = useState("");
  const [userId, setUserId] = useState("");
  const [queueId, setQueueId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [usersResponse, queuesResponse] = await Promise.all([
          // Sem pageSize o filtro so oferecia os 20 atendentes mais novos.
          api.get("/users", { params: { pageNumber: 1, pageSize: 200, orderBy: "name" } }),
          api.get("/queue")
        ]);
        setUsers(usersResponse.data?.users || []);
        setQueues(queuesResponse.data || []);
      } catch (error) {
        // Filtro por atendente e fila e conveniencia: se a carga falhar, a
        // pesquisa continua utilizavel pelos demais campos.
        setUsers([]);
        setQueues([]);
      }
    })();
  }, []);

  const fetchRatings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ticket-ratings", {
        params: {
          pageNumber,
          searchParam: appliedSearch || undefined,
          status: status || undefined,
          ratingValue: ratingValue || undefined,
          userId: userId || undefined,
          queueId: queueId || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo ? `${dateTo}T23:59:59.999` : undefined
        }
      });

      setRatings(prev =>
        pageNumber === 1 ? data.ratings : [...prev, ...data.ratings]
      );
      setCount(data.count);
      setHasMore(data.hasMore);
      setSummary(data.summary);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, [
    pageNumber,
    appliedSearch,
    status,
    ratingValue,
    userId,
    queueId,
    dateFrom,
    dateTo
  ]);

  useEffect(() => {
    fetchRatings();
  }, [fetchRatings]);

  const resetAnd = setter => value => {
    setPageNumber(1);
    setter(value);
  };

  const handleSearchSubmit = event => {
    event.preventDefault();
    setPageNumber(1);
    setAppliedSearch(searchParam.trim());
  };

  const maxDistribution = summary
    ? Math.max(1, ...Object.values(summary.distribution || {}))
    : 1;

  return (
    <MainContainer>
      <MainHeader>
        <Title>Avaliações</Title>
        <MainHeaderButtonsWrapper>
          <Typography variant="body2" color="textSecondary">
            {count} avaliac{count === 1 ? "ao" : "oes"}
          </Typography>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {summary ? (
          <div className={classes.summary}>
            <div className={classes.summaryCard}>
              <div className={classes.summaryValue}>
                {summary.average === null ? "-" : summary.average}
              </div>
              <Typography variant="caption" color="textSecondary">
                Nota media
              </Typography>
            </div>
            <div className={classes.summaryCard}>
              <div className={classes.summaryValue}>
                {summary.responseRate === null ? "-" : `${summary.responseRate}%`}
              </div>
              <Typography variant="caption" color="textSecondary">
                Taxa de resposta
              </Typography>
            </div>
            <div className={classes.summaryCard}>
              <div className={classes.summaryValue}>{summary.answered}</div>
              <Typography variant="caption" color="textSecondary">
                Respondidas
              </Typography>
            </div>
            <div className={classes.summaryCard}>
              <div className={classes.summaryValue}>
                {summary.pending + summary.expired + summary.failed}
              </div>
              <Typography variant="caption" color="textSecondary">
                Sem resposta
              </Typography>
            </div>

            <div className={classes.distribution}>
              {[5, 4, 3, 2, 1].map(score => {
                const value = summary.distribution?.[String(score)] || 0;
                return (
                  <div className={classes.distributionRow} key={score}>
                    <Typography variant="caption">{score}</Typography>
                    <div className={classes.barTrack}>
                      <div
                        className={classes.barFill}
                        style={{
                          width: `${(value / maxDistribution) * 100}%`
                        }}
                      />
                    </div>
                    <Typography variant="caption">{value}</Typography>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        <form className={classes.filters} onSubmit={handleSearchSubmit}>
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            placeholder="Buscar por contato, número ou comentario"
            value={searchParam}
            onChange={e => setSearchParam(e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton size="small" type="submit">
                  <Search fontSize="small" />
                </IconButton>
              )
            }}
          />
          <TextField
            select
            className={classes.filterField}
            variant="outlined"
            size="small"
            label="Situação"
            value={status}
            onChange={e => resetAnd(setStatus)(e.target.value)}
          >
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="answered">Respondida</MenuItem>
            <MenuItem value="sent">Enviada</MenuItem>
            <MenuItem value="pending">Aguardando</MenuItem>
            <MenuItem value="expired">Expirada</MenuItem>
            <MenuItem value="failed">Falha no envio</MenuItem>
          </TextField>
          <TextField
            select
            className={classes.filterField}
            variant="outlined"
            size="small"
            label="Nota"
            value={ratingValue}
            onChange={e => resetAnd(setRatingValue)(e.target.value)}
          >
            <MenuItem value="">Todas</MenuItem>
            {[1, 2, 3, 4, 5].map(score => (
              <MenuItem key={score} value={score}>
                {score}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            className={classes.filterField}
            variant="outlined"
            size="small"
            label="Atendente"
            value={userId}
            onChange={e => resetAnd(setUserId)(e.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            {users.map(item => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            className={classes.filterField}
            variant="outlined"
            size="small"
            label="Fila"
            value={queueId}
            onChange={e => resetAnd(setQueueId)(e.target.value)}
          >
            <MenuItem value="">Todas</MenuItem>
            {queues.map(item => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="date"
            className={classes.filterField}
            variant="outlined"
            size="small"
            label="De"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={e => resetAnd(setDateFrom)(e.target.value)}
          />
          <TextField
            type="date"
            className={classes.filterField}
            variant="outlined"
            size="small"
            label="Ate"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={e => resetAnd(setDateTo)(e.target.value)}
          />
        </form>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ticket</TableCell>
              <TableCell>Contato</TableCell>
              <TableCell>Atendente</TableCell>
              <TableCell>Fila</TableCell>
              <TableCell align="center">Nota</TableCell>
              <TableCell>Comentario</TableCell>
              <TableCell>Situação</TableCell>
              <TableCell>Respondida em</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ratings.map(rating => (
              <TableRow hover key={rating.id}>
                <TableCell>#{rating.ticketId}</TableCell>
                <TableCell>
                  {rating.contact?.name || "-"}
                  {rating.contact?.number ? ` (${rating.contact.number})` : ""}
                </TableCell>
                <TableCell>
                  {rating.user?.name || (
                    // userId nulo significa atendimento sem atendente humano
                    // atribuido - tipicamente o agente de IA.
                    <span className={classes.muted}>
                      {rating.handledByMode === "bot" ? "IA" : "sem atendente"}
                    </span>
                  )}
                </TableCell>
                <TableCell>{rating.queue?.name || "-"}</TableCell>
                <TableCell align="center">
                  {rating.ratingValue || "-"}
                </TableCell>
                <TableCell className={classes.comment}>
                  {rating.comment || ""}
                </TableCell>
                <TableCell>
                  {statusLabels[rating.status] || rating.status}
                </TableCell>
                <TableCell>{formatDateTime(rating.answeredAt)}</TableCell>
              </TableRow>
            ))}
            {loading ? <TableRowSkeleton columns={8} /> : null}
            {!loading && !ratings.length ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="textSecondary">
                    Nenhuma avaliação encontrada para os filtros selecionados.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>

        {hasMore ? (
          <Button
            fullWidth
            disabled={loading}
            onClick={() => setPageNumber(prev => prev + 1)}
          >
            Carregar mais
          </Button>
        ) : null}
      </Paper>
    </MainContainer>
  );
};

export default Ratings;
